import { DriverModel } from '@/app/models'
import * as React from 'react'
import { createContext, useContext } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import useDriverService from '@/modules/drivers/services/DriverService'
import { GroupModel } from '@/app/models/groupModel'
import { ViewModeType } from '@/modules/drivers/types'
import {
  useInfiniteQueryDrivers,
  useQueryDriverGroups,
  useQueryBasicDrivers,
  type BasicDriver,
} from '@/lib/api/company/drivers'
import { useSideSelectionStore } from '@/modules/drivers/stores/sideSelectionStore'

interface DriversContextProps {
  drivers: DriverModel[]
  allDrivers: DriverModel[]

  selectedIds: string[]
  setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>
  isSelectedAny: boolean
  driversIds: number[]

  actualGroup: GroupModel | null
  setActualGroup: React.Dispatch<React.SetStateAction<GroupModel | null>>
  groups: GroupModel[]
  activeViewMode: ViewModeType
  setActiveViewMode: React.Dispatch<ViewModeType>

  setSearch: any
  search: string
  searchResults: number
  showSearch: boolean
  setShowSearch: React.Dispatch<React.SetStateAction<boolean>>
  setSelectedDriverId: React.Dispatch<React.SetStateAction<number>>
  selectedDriverId: number

  refresh: () => void
  triggerRefresh: () => void
  refreshState: number
  fetchDriver: ({ driverId }: { driverId: number }) => Promise<DriverModel>

  totalCount: number

  isLoadingDrivers: boolean

  hasMore: boolean
  loadMore: () => void
  isLoadingMore: boolean

  selectedIndividualGroupId: number
  setSelectedIndividualGroupId: React.Dispatch<React.SetStateAction<number>>

  individualDrivers: DriverModel[]
  basicDrivers: BasicDriver[]
  isLoadingBasicDrivers: boolean
  isFetchingBasicDrivers: boolean
  actualGroupDriverIds: number[]
}

const DriversContext = createContext<DriversContextProps | null>(null)

export const DriversProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {

  const { pathname } = useLocation()
  const { companyId = '' } = useParams<{ companyId: string }>()

  const [refreshState, setRefreshState] = React.useState(0)
  const { getDriversInGroup, getDriver } = useDriverService()

  const [actualGroup, setActualGroup] = React.useState<GroupModel | null>({
    id: -1,
    name: 'Aktywni',
  })
  const [selectedIds, setSelectedIds] = React.useState<string[]>([])

  const [search, setSearch] = React.useState('')
  const [showSearch, setShowSearch] = React.useState(false)

  const isDriverRoute = React.useMemo(
    () =>
      pathname.includes('/driverModule') ||
      pathname.includes('/printouts') ||
      pathname.includes('/reports'),
    [pathname],
  )

  const activeViewMode = React.useMemo<ViewModeType>(() => {
    const isBusinessTripsGroupRoute = /\/business-trips\//.test(pathname)
    const isBusinessTripsSingleRoute = /\/driver\/\d+\/businessTrip\//.test(
      pathname,
    )
    if (
      isBusinessTripsSingleRoute ||
      (/driver\/\d+/.test(pathname) && !isBusinessTripsGroupRoute)
    ) {
      return 'single'
    }
    return 'group'
  }, [pathname])
  const setActiveViewMode = React.useCallback<
    React.Dispatch<ViewModeType>
  >(() => {}, [])

  const isActiveFilter = React.useMemo(() => {
    if (!actualGroup) return undefined
    if (actualGroup.id === -1) return true
    if (actualGroup.id === -2) return false
    return true
  }, [actualGroup])

  const {
    drivers: driversRaw,
    isLoading: isLoadingDrivers,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQueryDrivers({
    companyId,
    pageSize: 20,
    groupId: actualGroup?.id && actualGroup.id > 0 ? actualGroup.id : undefined,
    isActive: isActiveFilter,
    search: search && search.trim() !== '' ? search.trim() : undefined,
    enabled: false,
  })
  const drivers = driversRaw as unknown as DriverModel[]
  const allDrivers = drivers

  const driversIds = React.useMemo(
    () => Array.from(new Set(drivers.map((d) => d.id))),
    [drivers],
  )

  const [selectedIndividualGroupId, setSelectedIndividualGroupId] =
    React.useState(-1)
  const [driversIdsInGroup, setDriversIdsInGroup] = React.useState<number[]>([])
  React.useEffect(() => {
    if (selectedIndividualGroupId && selectedIndividualGroupId > 0) {
      getDriversInGroup({ companyId, groupId: selectedIndividualGroupId })
        .then((res) => setDriversIdsInGroup(res || []))
    } else {
      setDriversIdsInGroup([])
    }
  }, [selectedIndividualGroupId, companyId])

  const [actualGroupDriverIds, setActualGroupDriverIds] = React.useState<number[]>([])
  React.useEffect(() => {
    if (actualGroup && actualGroup.id > 0) {
      getDriversInGroup({ companyId, groupId: actualGroup.id })
        .then((res) => setActualGroupDriverIds(res || []))
    } else {
      setActualGroupDriverIds([])
    }
  }, [actualGroup, companyId])
  const individualDrivers = React.useMemo(() => {
    if (selectedIndividualGroupId === 0) return drivers
    if (selectedIndividualGroupId === -1) return drivers.filter((d) => d.isActive)
    if (selectedIndividualGroupId === -2) return drivers.filter((d) => !d.isActive)
    return drivers.filter((d) => driversIdsInGroup.includes(d.id) && d.isActive)
  }, [drivers, driversIdsInGroup, selectedIndividualGroupId])

  const isSelectedAny = selectedIds.length > 0
  const hasMore = !!hasNextPage
  const isLoadingMore = isFetchingNextPage
  const loadMore = React.useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])
  const searchResults = 0

  const { data: groupsData, refetch: refetchGroups } = useQueryDriverGroups({
    companyId,
  })
  const groups = groupsData ?? []
  React.useEffect(() => {
    setSearch('')
    setSelectedIds([])
    // Drop the shared side-panel selection when switching companies so stale
    // driver ids from a previous company can't bleed into the new one.
    if (companyId) useSideSelectionStore.getState().syncCompany(companyId)
  }, [companyId])

  const {
    data: basicDrivers = [],
    isLoading: isLoadingBasicDrivers,
    isFetching: isFetchingBasicDrivers,
    refetch: refetchBasicDrivers,
  } = useQueryBasicDrivers({ companyId })

  const { driverId: driverIdParam } = useParams<{ driverId?: string }>()

  const driverIdFromPath = React.useMemo(() => {
    const match = pathname.match(/\/driver\/(\d+)(?:\/|$)/)
    return match ? match[1] : undefined
  }, [pathname])
  const driverIdFromUrl = driverIdParam ?? driverIdFromPath
  const [manuallySelected, setManuallySelected] = React.useState(0)
  const basicDriverIds = React.useMemo(
    () => basicDrivers.map((d) => d.id),
    [basicDrivers],
  )

  React.useEffect(() => {
    const fromUrl = driverIdFromUrl ? Number(driverIdFromUrl) : 0
    if (fromUrl > 0 && fromUrl !== manuallySelected) {
      setManuallySelected(fromUrl)
    }
  }, [driverIdFromUrl, manuallySelected])
  const selectedDriverId = React.useMemo(() => {
    const fromUrl = driverIdFromUrl ? Number(driverIdFromUrl) : 0
    if (fromUrl > 0) return fromUrl
    if (manuallySelected > 0 && basicDriverIds.includes(manuallySelected)) {
      return manuallySelected
    }
    let candidates = basicDrivers
    if (actualGroup?.id === -1) {
      candidates = basicDrivers.filter((d) => d.isActive)
    } else if (actualGroup?.id === -2) {
      candidates = basicDrivers.filter((d) => !d.isActive)
    } else if (actualGroup && actualGroup.id > 0) {
      const groupIds = new Set(actualGroupDriverIds)
      candidates = basicDrivers.filter((d) => groupIds.has(d.id) && d.isActive)
    }
    // basicDrivers przychodzi z API juz posortowane alfabetycznie (ListDriversBasicRequestHandler:
    // OrderBy(Surname).ThenBy(Name)) i po tej samej kolejnosci chodza strzalki poprzedni/nastepny
    // (navList w DriverViewControlBarLayout). Sortowanie po id wybieralo kierowce o najnizszym id.
    return candidates[0]?.id ?? 0
  }, [driverIdFromUrl, manuallySelected, basicDriverIds, basicDrivers, actualGroup, actualGroupDriverIds])
  const setSelectedDriverId = setManuallySelected

  const fetchDriver = React.useCallback(
    async ({ driverId }: { driverId: number }) =>
      getDriver({ companyId, driverId, withPersonalData: true }),
    [companyId, getDriver],
  )

  const triggerRefresh = React.useCallback(() => {
    setRefreshState((prev) => prev + 1)
    if (companyId && isDriverRoute) {
      void refetch()
      void refetchBasicDrivers()
    }
  }, [companyId, isDriverRoute, refetch, refetchBasicDrivers])

  const refresh = React.useCallback(() => {
    void refetchGroups()
    void refetch()
    void refetchBasicDrivers()
  }, [refetchGroups, refetch, refetchBasicDrivers])

  return (
    <DriversContext.Provider
      value={{
        triggerRefresh,
        refreshState,
        drivers,
        allDrivers,
        selectedIds,
        setSelectedIds,
        driversIds,
        isSelectedAny,
        groups,
        actualGroup,
        setActualGroup,
        activeViewMode,
        setActiveViewMode,
        search,
        setSearch,
        searchResults,
        showSearch,
        setShowSearch,
        setSelectedDriverId,
        selectedDriverId,
        refresh,
        fetchDriver,
        totalCount: basicDrivers.length,
        isLoadingDrivers,
        hasMore,
        loadMore,
        isLoadingMore,
        selectedIndividualGroupId,
        setSelectedIndividualGroupId,
        individualDrivers,
        basicDrivers,
        isLoadingBasicDrivers,
        isFetchingBasicDrivers,
        actualGroupDriverIds,
      }}
    >
      {children}
    </DriversContext.Provider>
  )
}

export const useDrivers = () => {
  const ctx = useContext(DriversContext)
  if (!ctx) throw new Error('useDrivers must be used inside <DriversProvider>')
  return ctx
}
